import { ChevronRight, CircleCheck, CircleX, Pencil, TriangleAlert } from 'lucide-react'

import { cn } from '@fortem-monorepo/js-utilities'
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Separator,
} from '@fortem-monorepo/react-ui'
import { ICG2Guidelines, ICGG2Report } from '@fortem-monorepo/web-sdk'
import { ContentBlocks } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { PERMISSIONS } from '@/constants/permissions'
import { useGuidelineDrawer } from '@/pages/funds/ic-guidelines-v2/components/GuidelineDrawerContext'
import { GuidelineIssues } from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/GuidelineIssues'
import { GuidelineRowEvaluations } from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/GuidelineRowEvaluations'
import { GuidelineRowSnippet } from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/GuidelineRowSnippet'
import { GuidelineRowThresholdRange } from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/GuidelineRowThresholdRange'
import { GuidelineTags } from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/GuidelineTags'

import { getGuidelineIndicatorValues } from './guideline-value-scale'

const statusMap: Partial<
  Record<
    ICG2Guidelines.GuidelineStatus,
    (guideline: ICGG2Report.GuidelineResult) => { label: string; classNames: string }
  >
> = {
  ok: () => ({
    label: 'Pass',
    classNames: 'bg-green-100 border-green-300 text-green-600',
  }),
  hard: () => ({
    label: 'Hard breach',
    classNames: 'bg-red-100 border-red-300 text-red-600',
  }),
  soft: () => ({
    label: 'Soft breach',
    classNames: 'bg-amber-100 border-amber-200 text-amber-600',
  }),
  failed: (guideline) => ({
    label: `${guideline.issues.length} issue${guideline.issues.length > 1 ? 's' : ''}`,
    classNames: 'bg-blue-100 border-blue-300 text-blue-600',
  }),
}

export interface GuidelineRowProps {
  item: ICGG2Report.GuidelineResult
  sourceBlock: ContentBlocks.DataGridContentBlock | null
}

export const GuidelineRow: React.FC<GuidelineRowProps> = ({ item, sourceBlock }) => {
  const { openEdit } = useGuidelineDrawer()
  const statusInfo = statusMap[item.status]?.(item)
  const showEvaluations = item.rows.length > 0

  return (
    <Collapsible asChild>
      <div className={cn('group flex border rounded-lg overflow-hidden')}>
        <div
          className={cn('min-w-1 min-h-full', {
            'bg-green-400': item.status === 'ok',
            'bg-red-400': item.status === 'hard',
            'bg-amber-400': item.status === 'soft',
            'bg-blue-400': item.status === 'failed',
          })}
        />
        <div className={'flex-1 overflow-hidden'}>
          <CollapsibleTrigger asChild className={cn({})}>
            <div
              className={
                'header p-4 flex items-center gap-2 justify-between border-b bg-sidebar hover:bg-gray-100 cursor-pointer'
              }
            >
              <div className="flex items-center gap-4">
                {item.status === 'ok' && <CircleCheck size={16} className={'text-green-500'} />}
                {item.status === 'hard' && <CircleX size={16} className={'text-red-500'} />}
                {item.status === 'soft' && <CircleX size={16} className={'text-amber-500'} />}
                {item.status === 'failed' && (
                  <TriangleAlert size={16} className={'text-blue-500'} />
                )}
                <span className={'text-sm font-medium text-[#333333]'}>{item.guideline.name}</span>
              </div>
              <div className="flex items-center gap-4">
                <GuidelineRowSnippet result={item} />
                <div className={cn('text-xs px-2 py-1 border rounded-sm', statusInfo?.classNames)}>
                  {statusInfo?.label}
                </div>
                <ChevronRight
                  className={'size-4 transition-transform group-data-[state=open]:rotate-90'}
                />
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent asChild>
            <div className={'flex flex-col p-4 gap-4'}>
              <div>
                <CanAccess permissions={[PERMISSIONS.WRITE_IC_GUIDELINES]}>
                  <Button
                    variant={'outline'}
                    size="sm"
                    className={'text-[#333333]'}
                    onClick={() => openEdit(item.guideline.id!)}
                  >
                    <Pencil /> Edit guideline
                  </Button>
                </CanAccess>
              </div>
              {item.guideline.description && (
                <span className={'text-sm text-muted-foreground'}>
                  {item.guideline.description}
                </span>
              )}
              <GuidelineTags guideline={item.guideline} />
              {item.status !== 'failed' && (
                <GuidelineRowThresholdRange
                  thresholds={item.guideline.thresholds}
                  thresholdType={item.guideline.threshold_type}
                  values={getGuidelineIndicatorValues(item.guideline, item.rows, item.aggregate)}
                />
              )}
              <GuidelineIssues result={item} />
              {showEvaluations && (
                <>
                  <Separator />
                  <GuidelineRowEvaluations result={item} sourceBlock={sourceBlock} />
                </>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </div>
    </Collapsible>
  )
}
